<template>
  <va-card id="availabilities-page">
    <div class="d-flex justify-content-between align-items-center">
      <h3 class="mb-0">
        {{$t('agenda.appointments.title')}}
      </h3>
    </div>

    <div class="row">
      <div class="col">
        <div>
          <label class="form-label">{{$t('clinical.appointments.select_facility')}}:</label>
          <div class="form-group" style="margin-bottom:1.25rem;">
            <div class="input-group">
              <multiselect
                v-model="selectedFacility"
                :options="facilities"
                track-by="id"
                label="name"
                select-label=""
                selected-label=""
                deselect-label=""
                placeholder="---"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                :disabled="facilities.length < 2"
                >
              </multiselect>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <FullCalendar
          ref="fullCalendar"
          :options="calendarOptions">
          <template v-slot:eventContent='data'>
            <EventDisplay
              v-if="data.event.display !== 'background'"
              :date="data.timeText"
              :view="data.view"
              :event="data.event"
              />
          </template>
        </FullCalendar>
      </div>
    </div>

    <AppointmentForm
      v-if="showAppointmentForm"
      v-bind="propsAppointmentForm"
      @cancel="appointmentFormCanceled"
      @appointment-created="appointmentCreated"
      @appointment-updated="appointmentUpdated"
      @appointment-deleted="appointmentDeleted"
      @campaign-added="campaignAdded"
      />

  </va-card>
</template>
<script>
import moment from 'moment'
import EventDisplay from './EventDisplay'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import momentPlugin from '@fullcalendar/moment'
import Multiselect from 'vue-multiselect'
import AppointmentForm from './AppointmentForm'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import { AppointmentService, FacilityService, AvailabilityService } from '@/api/BookingService'
import { CALENDAR_APPOINTMENTS_FACILITY, FILTERS_CALENDAR_VIEW, FILTERS_CALENDAR_DAY } from '@/local-storage/LocalStorageKeys'

const AppointmentsInfoType = 400

export default {
  components: {
    EventDisplay,
    FullCalendar,
    Multiselect,
    AppointmentForm
  },
  props: {
    component: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      facilities: [],
      selectedFacilities: [],
      selectedFacility: null,
      calendarAPI: null,
      events: [],
      needsToLoad: false,
      previousBusinessHours: [],
      calendarOptions: {
        eventSources: [{
          events: (options, callback) => {
            if (!this.selectedFacility) {
              return
            }

            let params = {
              startDate: moment(options.start).format('YYYY-MM-DD'),
              endDate: moment(options.end).format('YYYY-MM-DD'),
              with: 'creator,appointments,appointments.type,appointments.status',
              facilities: this.selectedFacility.id
            }

            AvailabilityService.query(params)
              .then(availabilities => {
                let view = this.calendarAPI.view
                let businessHours = []
                if (!availabilities.length && view.type !== 'dayGridMonth') {
                  businessHours = [{
                    daysOfWeek: [moment(options.start).day()],
                    startTime: '05:00',
                    endTime: '05:30'
                  }]
                }
                availabilities = availabilities.map(availability => {
                  availability.appointments = availability.appointments.map(appointment => {
                    return {
                      ...appointment,
                      start: this.$options.filters.localTime(appointment.start),
                      end: this.$options.filters.localTime(appointment.end),
                      constraint: {
                        startTime: moment(availability.start).format('HH:mm'),
                        endTime: moment(availability.end).format('HH:mm'),
                        daysOfWeek: [moment(availability.start).day()]
                      }
                    }
                  })
                  return {
                    ...availability,
                    start: this.$options.filters.localTime(availability.start),
                    end: this.$options.filters.localTime(availability.end),
                    overlap: true,
                    display: 'background',
                    backgroundColor: 'lightblue',
                  }
                })

                let appointments = []
                availabilities.forEach(availability => {
                  appointments.push(...availability.appointments)
                })
                if (view.type !== 'dayGridMonth') {
                  if (availabilities.length) {
                    businessHours = availabilities
                      .filter(event => event.display === 'background')
                      .map(availability => {
                        return {
                          daysOfWeek: [moment(availability.start).day()],
                          startTime: moment(availability.start).format('HH:mm'),
                          endTime: moment(availability.end).format('HH:mm')
                        }
                      })
                  }
                } else {
                  businessHours = []
                }

                if (this.hasDifferentBusinessHours(businessHours)) {
                  this.previousBusinessHours = businessHours
                  this.calendarAPI.setOption('businessHours', businessHours)
                }

                let events = [...availabilities, ...appointments]
                this.events = events

                callback(events)
              })
              .catch(errors => console.log('errors', errors))
          }
        }],
        plugins: [dayGridPlugin, timeGridPlugin, momentPlugin, interactionPlugin],
        initialView: LocalStorageHelper.get(FILTERS_CALENDAR_VIEW) || 'dayGridMonth',
        initialDate: LocalStorageHelper.get(FILTERS_CALENDAR_DAY, false) || moment().format('YYYY-MM-DD'),
        businessHours: [],
        eventColor: '#29c4a9',
        eventBorderColor: '#29c4a9',
        headerToolbar: {
          left: 'prev next today',
          center: 'title',
          right: 'timeGridDay timeGridWeek dayGridMonth'
        },
        eventDisplay: 'block',
        locale: this.selectedLanguage,
        // selectConstraint: 'businessHours',
        buttonText: {
          today: this.$t('agenda.buttons.today'),
          month: this.$t('agenda.buttons.month'),
          week: this.$t('agenda.buttons.week'),
          day: this.$t('agenda.buttons.day'),
          listMonth: this.$t('agenda.buttons.listMonth')
        },
        customButtons: {
          timeGridDay: {
            text: this.$t('agenda.buttons.day'),
            click: (event) => {
              this.calendarAPI.gotoDate(this.getNextViewStart())
              this.calendarAPI.changeView('timeGridDay')
            }
          },
          timeGridWeek: {
            text: this.$t('agenda.buttons.week'),
            click: () => {
              this.calendarAPI.gotoDate(this.getNextViewStart())
              this.calendarAPI.changeView('timeGridWeek')
            }
          }
        },
        lazyFetching: false,
        allDaySlot: false,
        allDayContent: this.$t('agenda.buttons.all-day'),
        allDayClassNames: 'calendar__allday__label',
        slotMinTime: '09:00:00',
        slotMaxTime: '23:00:00',
        displayEventEnd: true,
        nowIndicator: true,
        slotDuration: '00:30',
        slotLabelFormat: 'HH:mm',
        slotLabelInterval: '01:00',
        height: 'auto',
        dayHeaderFormat: 'ddd D',
        eventTimeFormat: 'H:mm',
        firstDay: 1,
        navLinks: true,
        dayHeaderContent: (content) => {
          return { html: `
            <div class="calendar__header__label">
              <div class="calendar__header__label__day view__${content.view.type}">` + moment(content.date).locale(this.selectedLanguage === 'gb' ? 'en' : this.selectedLanguage).format('DD') + `</div>
              <div class="calendar__header__label__weekday">` + moment(content.date).locale(this.selectedLanguage === 'gb' ? 'en' : this.selectedLanguage).format('dddd') + `</div>
            </div>
          ` }
        },
        titleFormat: 'MMMM, YYYY',
        editable: true,
        weekends: true,
        selectable: true,
        selectMirror: true,
        unselectAuto: true,
        dateClick: this.eventCreated,
        eventClick: this.eventSelected,
        eventDrop: this.updateEventFromUI,
        eventResize: this.updateEventFromUI,
        select: this.eventCreated,
        datesDidUpdate: this.datesDidUpdate
        // selectConstraint: 'businessHours',
      },
      showAppointmentForm: false
    }
  },
  created () {
    this.loadAccessableFacilities()
  },
  mounted () {
    this.calendarAPI = this.$refs.fullCalendar.getApi()
  },
  methods: {
    loadCalendarOptions: function () {
      if (this.calendarAPI) {
        this.calendarAPI.setOption('locale', this.selectedLanguage === 'gb' ? 'en' : this.selectedLanguage)
      }
      return Promise.resolve()
    },
    getNextViewStart: function () {
      let start = moment(this.calendarAPI.view.currentStart)
      let end = moment(this.calendarAPI.view.currentEnd)
      let now = moment()
      if (start.isSameOrBefore(now) && end.isSameOrAfter(now)) {
        return now.toISOString()
      }
      return start.toISOString()
    },
    datesDidUpdate: function () {
      this.$nextTick(() => {
        LocalStorageHelper.set(FILTERS_CALENDAR_VIEW, this.calendarAPI.view.type)
        LocalStorageHelper.set(FILTERS_CALENDAR_DAY, moment(this.calendarAPI.view.currentStart).format('YYYY-MM-DD'), false)
      })
    },
    hasDifferentBusinessHours: function (businessHours) {
      return JSON.stringify(businessHours) !== JSON.stringify(this.previousBusinessHours)
    },
    hasPermissionsTo (facility) {
      return this.permissions.some(permission => permission.access_type >= 2 && (permission.entity_id === null || permission.entity_id === facility.entity_id))
    },
    appointmentFormCanceled: function () {
      this.propsAppointmentForm = {}
      this.showAppointmentForm = false
    },
    appointmentCreated: function ({ appointment }) {
      this.propsAppointmentForm = {}
      this.showAppointmentForm = false
      this.loadEvents()
    },
    appointmentUpdated: function () {
      this.propsAppointmentForm = {}
      this.showAppointmentForm = false
      this.loadEvents()
    },
    appointmentDeleted: function () {
      this.propsAppointmentForm = {}
      this.showAppointmentForm = false
      this.loadEvents()
    },
    campaignAdded: function ({ availability, appointments }) {
      this.loadEvents()
      this.selectedAvailability.appointments = this.selectedAvailability.appointments.concat(appointments)
      this.selectedAvailability.appointments.sort((a, b) => {
        return (new Date(a.start)) - (new Date(b.start))
      })
    },
    nameWithCity ({ name, address }) {
      if (!address) return name
      return name + ' (' + address.city + ')'
    },
    loadHolidays () {
      this.$store.dispatch('loadHolidays', this.component.service.url)
        .then(() => {
          this.holidays = this.$store.state.ebooking.holidays
        })
    },
    eventSelected: function (data) {
      let { event, view } = data

      if (!this.hasPermissionsTo(event.extendedProps.facility)) {
        return
      }

      this.selectedAvailability = this.events.find(_event => {
        return _event.display === 'background' && _event.id === event.extendedProps.availability_id
      })

      this.propsAppointmentForm = {
        selectedAppointment: {
          id: parseInt(event.id),
          start: moment(event.start).format('YYYY-MM-DD HH:mm:ss'),
          end: moment(event.end).format('YYYY-MM-DD HH:mm:ss'),
          ...event.extendedProps
        },
        availability: {
          ...this.selectedAvailability,
          start: view.type.toLowerCase() === 'month' ? this.selectedAvailability.start_time : this.selectedAvailability.start,
          end: view.type.toLowerCase() === 'month' ? this.selectedAvailability.end_time : this.selectedAvailability.end
        }
      }

      this.showAppointmentForm = true
    },
    loadAccessableFacilities () {
      Promise.all([
        FacilityService.access({ access: 1, info: 400 }),
        FacilityService.activities()
      ])
        .then((facilities) => {
          this.facilities = facilities[0]
          facilities[1].forEach(facility => {
            if (!this.facilities.some(_facility => _facility.id === facility.id)) {
              this.facilities.push(facility)
            }
          })

          // We need to provide a title for calendar resources
          this.facilities = this.facilities.map(facility => {
            return {
              ...facility,
              title: facility.name
            }
          })
        })
        .then(() => this.updateFromLocalStorage())
        .then(() => this.loadCalendarOptions())
        .then(() => this.loadEvents())
        .catch((err) => {
          console.log(err)
        })
    },
    updateFromLocalStorage: function () {
      let previousFacility = LocalStorageHelper.get(CALENDAR_APPOINTMENTS_FACILITY)
      if (previousFacility) {
        this.selectedFacility = this.facilities.find(facility => facility.id === parseInt(previousFacility))
      } else if (this.facilities.length < 2) {
        this.selectedFacility = this.facilities[0]
      }
      return Promise.resolve()
    },
    loadEvents: function () {
      this.calendarAPI.refetchEvents()
    },
    eventCreated (data) {
      let { start, end, startStr, view } = data

      if (view.type !== 'dayGridMonth') {
        start = moment(start)
        end = moment(end)

        let selectedAvailability = this.events.find(event => {
          return event.display === 'background' && event.facility.id === this.selectedFacility.id && moment(event.start).isSameOrBefore(start) && moment(event.end).isSameOrAfter(end)
        })

        if (selectedAvailability) {
          if (!this.hasPermissionsTo(selectedAvailability.facility)) {
            return
          }

          this.propsAppointmentForm = {
            fromEvent: {
              start: start.format('YYYY-MM-DD HH:mm:ss'),
              end: end.format('YYYY-MM-DD HH:mm:ss')
            },
            availability: selectedAvailability
          }

          this.showAppointmentForm = true
        } else {
          this.calendarAPI.unselect()
        }
      } else {
        this.calendarAPI.gotoDate(moment(startStr).toISOString())
        this.calendarAPI.changeView('timeGridWeek')
        return false
      }
    },
    updateEventFromUI ({ event, revert }) {
      if (!this.hasPermissionsTo(event.extendedProps.facility)) {
        revert()
        return
      }

      let data = {
        start: this.$options.filters.utcTime(event.start),
        end: this.$options.filters.utcTime(event.end)
      }

      AppointmentService.update(event.id, data)
        .then(() => {
          this.showToast(this.$t('agenda.appointments.actions.updated'), this.$store.state.successToastOptions)
        }).catch((errors) => {
          revert()
          this.showToast(this.$t('agenda.appointments.errors.date_interval_dont_match_availability_interval', { start: data.start, end: data.end }), this.$store.state.errorToastOptions)
          console.log('errors', errors)
        })
    }
  },
  computed: {
    permissions: function () {
      return this.component.service.user_permissions.filter(permission => permission.info_type === AppointmentsInfoType)
    },
    selectedLanguage: function () {
      return this.$i18n.locale()
    }
  },
  watch: {
    selectedFacility: function (facility) {
      if (facility && this.calendarAPI) {
        this.loadEvents()
      }
      LocalStorageHelper.set(CALENDAR_APPOINTMENTS_FACILITY, facility.id, false)
    }
  }
}
</script>
<style lang="scss">
.fc-non-business{
  background-color: #a6a6a6 !important;
}
</style>
<style lang="scss">
  .fc-event-main {
    overflow-y: hidden;
  }
// @mixin for-desktop {
//   @media (min-width: 599px) {
//     @content;
//   }
// }

// .fc-day-grid-event > .fc-content {
//    white-space: normal;
//    text-overflow: ellipsis;
//    max-height:20px;
// }
// .fc-day-grid-event > .fc-content:hover {
//   max-height:none !important;
// }
// .calendar__allday__label {
//   font-size: 0.9rem;
// }

// .fc-header-left, fc-header-center, fc-header-right {
//     width: 100%;
//     display: block;
// }

// .floating__button {
//   position: fixed;
//   z-index: 9999;
//   color: #29c4a9;
//   font-size: 2rem;
// }

// .calendar__header__label {
//   display: flex;
//   flex-direction: column;
//   justify-content:center;
//   align-items: center;

//   & .calendar__header__label__weekday {
//     font-size: 0.6rem;
//     text-transform: uppercase;
//     @include for-desktop {
//       font-size: 1.3rem;
//     }
//   }

//   & .calendar__header__label__day {
//     font-size: 0.8rem;
//     font-weight: bold;
//     @include for-desktop {
//       font-size: 1.8rem;
//     }

//     &.view__dayGridMonth {
//       display: none !important;
//     }
//   }
// }

// th.fc-day-today {
//   background-color: #e0e0e0;
// }

// table.fc-scrollgrid {
//   // border: 0.5px solid #d3d3d3 !important;
//   // border-style: double !important;
//   // border-width: 0px !important;
//   // border-color: #aaa !important;
// }

// // .fc td, .fc th, .fc-timegrid, .fc-timeGridWeek-view, .fc-view {
// //   border-style: double !important;
// //   border-width: 0px !important;
// //   border-color: #aaa !important;
// // }

// td.fc-timegrid-slot {
//   border: 0.5px solid #d3d3d3 !important;
// }

// .fc-col-header-cell {
//   border-style: double !important;
//   border-width: 0px !important;
//   border-color: #aaa !important;
// }

// .fc-scrollgrid-section-header {
//   border-left-style: double !important;
//   border-left-width: 0px !important;
//   border-left-color: #aaa !important;
// }

// .fc-timegrid-slot {
//   height: 50px !important;
// }

// tr:first-child {
//   border: none !important;
// }

// .fc-timegrid-slot-label{
//   border: none !important;
// }

// .fc-button-primary {
//   color: #fff;
//   background-color: #29c4a9;
//   border-color: #29c4a9;
// }
</style>
